import React from 'react'
import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import Footer from '../components/Footer'

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="container-padding py-12 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-sm text-gray-600 mb-8">Last Updated: December 28, 2025</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                Welcome to FlowBeatIQ ("Service"), operated by Rigel Labs LLC ("Company", "we", "our", or "us").
                By accessing or using our Service, you agree to be bound by these Terms of Service ("Terms").
                If you do not agree to these Terms, please do not use our Service.
              </p>
              <p className="text-gray-700">
                These Terms constitute a legally binding agreement between you (whether personally or on behalf
                of an entity) and Rigel Labs LLC concerning your access to and use of the FlowBeatIQ platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                FlowBeatIQ is a privacy-compliant foot traffic analytics platform that provides small and
                medium-sized businesses with insights about visitor patterns and behavior using WiFi hardware sensors.
              </p>
              <p className="text-gray-700">
                Our Service includes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>WiFi-based visitor detection and counting</li>
                <li>Real-time and historical analytics dashboards</li>
                <li>Anomaly detection and alerts</li>
                <li>Weather correlation and AI-powered insights</li>
                <li>Multi-location management capabilities</li>
                <li>Hardware sensor provisioning and management</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts and Registration</h2>
              <p className="text-gray-700 mb-4">
                <strong>3.1 Account Creation:</strong> To use our Service, you must create an account by providing
                accurate and complete information, including your business name, email address, and password.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of
                your account credentials and for all activities that occur under your account. You agree to notify
                us immediately of any unauthorized access or use of your account.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>3.3 Account Accuracy:</strong> You agree to provide accurate, current, and complete information
                and to update such information to maintain its accuracy.
              </p>
              <p className="text-gray-700">
                <strong>3.4 Eligibility:</strong> You must be at least 18 years old and have the legal authority to
                enter into this agreement on behalf of your business.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Payment Terms</h2>
              <p className="text-gray-700 mb-4">
                <strong>4.1 Subscription Plans:</strong> FlowBeatIQ offers various subscription plans with different
                features and pricing tiers. Current pricing is available on our website and may be changed with notice.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>4.2 Paid Trial Period:</strong> We offer a 14-day risk-free trial period. When you sign up, 
                you will be charged immediately for your first month of service (currently $99 for the base plan). This is 
                a <strong>paid trial</strong>, not a free trial. During the 14-day risk-free period, you may cancel your 
                subscription as described in section 4.6. However, cancellation does not automatically entitle you to a refund. 
                See section 4.5 for our refund policy.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>4.3 Billing:</strong> Subscription fees are billed in advance on a monthly basis. Your first payment 
                is due immediately upon signup. Subsequent payments will be charged automatically on your monthly anniversary 
                date unless you cancel your subscription. All fees are non-refundable except as explicitly stated in section 4.5 
                or as required by law.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>4.4 Payment Processing:</strong> Payments are processed securely through Stripe. You agree to
                provide valid payment information and authorize us to charge your payment method for all fees incurred under 
                your account.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>4.5 Refund Policy:</strong> All fees are non-refundable except in the following circumstances: 
                (a) if you cancel within the 14-day risk-free period AND your hardware sensor has not yet shipped, you may 
                request a full refund by contacting support@flowbeatiq.com; (b) if your hardware sensor arrives damaged or 
                defective and cannot be replaced, you may request a refund; or (c) as required by applicable law. Refund 
                requests must be submitted within 30 days of the original charge. Approved refunds will be processed within 
                5-10 business days.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>4.6 Cancellation:</strong> You may cancel your subscription at any time through your account
                settings or by contacting support@flowbeatiq.com. Cancellation will take effect at the end of your current 
                billing period (cancel at period end). You will retain access to the Service until the end of your paid period. 
                You will not be charged again after cancellation unless you resubscribe. No refunds will be issued for partial 
                months of service except as stated in section 4.5.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>4.7 Hardware Shipment:</strong> Physical hardware sensors are shipped only after successful payment 
                processing. You acknowledge that the hardware represents a significant upfront cost to us, which is why we 
                require immediate payment and why refunds are limited to cases where hardware has not yet shipped. Once 
                hardware has shipped, all sales are final except for defective or damaged units as described in section 4.5.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>4.8 Failed Payments:</strong> If a payment fails, we will attempt to contact you and retry
                the payment. Continued failure to pay may result in suspension or termination of your account.
              </p>
              <p className="text-gray-700">
                <strong>4.9 Arbitration Opt-Out:</strong> You may opt out of the arbitration and class action waiver
                provisions by providing written notice to FlowBeatIQ within thirty (30) days of account creation.
                Opt-out notices must be sent to support@flowbeatiq.com and include your account email and business name.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Collection and Privacy</h2>
              <p className="text-gray-700 mb-4">
                <strong>5.1 Privacy-Compliant Analytics:</strong> Our Service collects anonymized WiFi probe data
                to provide foot traffic analytics. We do not collect personally identifiable information (PII)
                from visitors to your business.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>5.2 Data Processing:</strong> We process MAC addresses using one-way hashing to ensure
                visitor privacy while enabling accurate analytics. Hash values are device-specific and rotate
                periodically to prevent long-term tracking.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>5.3 Your Data:</strong> You retain ownership of all business data and analytics generated
                through your use of the Service. We will not sell, rent, or share your data with third parties
                except as described in our <Link to="/privacy" className="text-blue-600 hover:text-blue-700">Privacy Policy</Link>.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>5.4 Privacy Policy:</strong> Our collection and use of personal information is described in
                our <Link to="/privacy" className="text-blue-600 hover:text-blue-700">Privacy Policy</Link>, which
                is incorporated into these Terms by reference.
              </p>
              <p className="text-gray-700">
                Processing of visitor analytics data is governed by our{' '}
                <Link to="/dpa" className="text-blue-600 hover:text-blue-700">Data Processing Addendum (DPA)</Link>,
                which forms part of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property Rights</h2>
              <p className="text-gray-700 mb-4">
                <strong>6.1 Our Property:</strong> The Service, including all content, features, functionality,
                software, designs, text, graphics, logos, and trademarks (collectively, "Content") is owned by
                Rigel Labs LLC and is protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>6.2 Limited License:</strong> We grant you a limited, non-exclusive, non-transferable,
                revocable license to access and use the Service for your business purposes in accordance with these Terms.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>6.3 Restrictions:</strong> You may not:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Copy, modify, distribute, sell, or lease any part of the Service</li>
                <li>Reverse engineer or attempt to extract the source code of the Service</li>
                <li>Remove or alter any proprietary notices or labels</li>
                <li>Use the Service to build a competitive product or service</li>
                <li>Access the Service through automated means (bots, scrapers, etc.) without permission</li>
              </ul>
              <p className="text-gray-700">
                <strong>6.4 Trademarks:</strong> "FlowBeatIQ" and associated logos are trademarks of Rigel Labs LLC.
                You may not use these trademarks without our prior written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Service Availability and Support</h2>
              <p className="text-gray-700 mb-4">
                <strong>7.1 Uptime:</strong> While we target an uptime level of approximately 99.9%, we do not guarantee
                uninterrupted access to the Service. Scheduled maintenance will be announced in advance when possible.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>7.2 Support:</strong> We provide email support at support@flowbeatiq.com. Response times
                vary based on your subscription plan and the nature of your inquiry.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>7.3 Service Modifications:</strong> We reserve the right to modify, suspend, or discontinue
                any part of the Service at any time with reasonable notice.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>7.4 Hardware Ownership and Responsibility:</strong> Hardware sensors provided as part of the Service
                remain the property of Rigel Labs LLC. Customer is responsible for the care, custody, and control of the
                hardware while in their possession and is liable for loss, theft, or damage beyond normal wear and tear.
                Hardware must be returned upon cancellation or termination if requested.
              </p>
              <p className="text-gray-700">
                <strong>7.5 Hardware Disclaimer:</strong> Hardware is provided "AS IS" and "AS AVAILABLE," except where
                warranties are required by applicable law. Rigel Labs LLC disclaims all other warranties, express or implied,
                including fitness for a particular purpose.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. User Responsibilities and Prohibited Conduct</h2>
              <p className="text-gray-700 mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Upload or transmit viruses, malware, or malicious code</li>
                <li>Harass, abuse, or harm other users or our employees</li>
                <li>Impersonate any person or entity</li>
                <li>Collect or store personal data about other users without consent</li>
                <li>Use the Service in a manner that violates privacy laws or visitor expectations</li>
                <li>Ensure compliance with all applicable privacy, notice, consent, and data protection laws at their business locations, including requirements related to WiFi-based analytics and visitor disclosures</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers and Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                <strong>9.1 "AS IS" Service:</strong> THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>9.2 No Guarantee of Accuracy:</strong> While we strive for accuracy, we do not guarantee
                that the analytics, visitor counts, or insights provided by the Service are 100% accurate or complete.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>9.3 Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, RIGEL LABS LLC
                SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
                BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR
                USE OF THE SERVICE.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>9.4 Maximum Liability:</strong> OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR
                RELATED TO THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM.
              </p>
              <p className="text-gray-700">
                <strong>9.5 No Reliance:</strong> Analytics, insights, and recommendations provided by the Service are for
                informational purposes only and do not constitute business, legal, or financial advice. Customer assumes all
                risk associated with business decisions made based on the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify, defend, and hold harmless Rigel Labs LLC, its officers, directors, employees,
                and agents from and against any claims, liabilities, damages, losses, costs, or expenses (including
                reasonable attorneys' fees) arising out of or related to: (a) your use of the Service; (b) your
                violation of these Terms; (c) your violation of any rights of another party; or (d) any content or
                data you submit through the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
              <p className="text-gray-700 mb-4">
                <strong>11.1 Termination by You:</strong> You may terminate your account at any time by canceling
                your subscription through your account settings or by contacting us at support@flowbeatiq.com.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>11.2 Termination by Us:</strong> We reserve the right to suspend or terminate your account
                and access to the Service at any time, with or without notice, for violations of these Terms,
                non-payment, or any other reason.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>11.3 Effect of Termination:</strong> Upon termination, your right to use the Service will
                immediately cease. We may delete your account data after a reasonable period, though we may retain
                certain data as required by law or for legitimate business purposes.
              </p>
              <p className="text-gray-700">
                <strong>11.4 Survival:</strong> Sections of these Terms that by their nature should survive termination
                (including payment obligations, disclaimers, limitation of liability, and indemnification) will remain
                in effect after termination.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by
                email or through a notice on the Service at least 30 days before the changes take effect.
              </p>
              <p className="text-gray-700">
                Your continued use of the Service after the effective date of revised Terms constitutes your
                acceptance of the changes. If you do not agree to the revised Terms, you must stop using the Service
                and may cancel your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                <strong>13.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance
                with the laws of the State of Texas, United States, without regard to its conflict of law provisions.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>13.2 Dispute Resolution:</strong> Any dispute arising out of or relating to these Terms or
                the Service shall be resolved through binding arbitration in accordance with the American Arbitration
                Association's rules, conducted in Austin, Texas.
              </p>
              <p className="text-gray-700">
                <strong>13.3 Class Action Waiver:</strong> You agree to resolve disputes with us on an individual
                basis and waive your right to participate in class actions or class arbitrations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. General Provisions</h2>
              <p className="text-gray-700 mb-4">
                <strong>14.1 Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute
                the entire agreement between you and Rigel Labs LLC regarding the Service.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>14.2 Severability:</strong> If any provision of these Terms is found to be invalid or
                unenforceable, the remaining provisions will continue in full force and effect.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>14.3 Waiver:</strong> Our failure to enforce any right or provision of these Terms will not
                be considered a waiver of those rights.
              </p>
              <p className="text-gray-700">
                <strong>14.4 Assignment:</strong> You may not assign or transfer these Terms or your account without
                our prior written consent. We may assign our rights and obligations without restriction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-gray-700"><strong>Rigel Labs LLC</strong></p>
                <p className="text-gray-700">Email: support@flowbeatiq.com</p>
                <p className="text-gray-700">Website: https://flowbeatiq.com</p>
              </div>
            </section>

            <div className="border-t border-gray-200 pt-8 mt-8">
              <p className="text-sm text-gray-600 mb-4">
                By using FlowBeatIQ, you acknowledge that you have read, understood, and agree to be bound by
                these Terms of Service.
              </p>
              <Link
                to="/"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default TermsOfService
